// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useOverflow.ts
 * @input Uses React useState, useCallback, useRef; useIsomorphicLayoutEffect
 * @output Exports useOverflow hook for measuring and managing horizontal overflow
 * @position Core hook; used by OverflowList and consumers for overflow patterns
 *
 * Measures children rendered in a hidden container to determine how many fit
 * in the available width, without flickering. Uses ResizeObserver to react
 * to container size changes. Supports an optional item cap (`maxVisibleItems`)
 * and bounded multi-row wrapping (`maxRows`); the fit/clamp/row-packing math
 * lives in the pure `computeOverflow` helper.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/hooks/index.ts
 * - /packages/core/src/hooks/computeOverflow.ts (the pure computation)
 */
import { useState, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect.js";
import { observeResize, unobserveResize } from "../utils/sharedResizeObserver.js";
import { computeOverflow } from "./computeOverflow.js";
import { useDevWarning } from "./useDevWarning.js";
/**
 * Hook for managing horizontal overflow of a list of items.
 *
 * Renders all items into a hidden measurement container, then calculates
 * how many fit in the visible container's width. Uses ResizeObserver to
 * recalculate when the container resizes.
 *
 * The measurement container should contain all items followed by the
 * overflow indicator element (if any). The indicator is identified by
 * a `data-overflow-indicator` attribute.
 *
 * @example
 * ```
 * const { containerRef, measureRef, visibleCount, hasOverflow } = useOverflow(5, {
 *   gap: 8,
 * });
 * ```
 */
export function useOverflow(itemCount, options = {}) {
  const {
    gap = 0,
    minVisibleItems = 0,
    maxVisibleItems,
    maxRows,
    collapseFrom = 'end',
    behavior = 'observeSelf'
  } = options;
  useDevWarning('useOverflow', `maxVisibleItems (${maxVisibleItems}) is less than ` + `minVisibleItems (${minVisibleItems}); the floor wins and ` + `minVisibleItems items will be shown.`, maxVisibleItems != null && maxVisibleItems < minVisibleItems);
  const observeParent = behavior === 'observeParent';
  const [visibleCount, setVisibleCount] = useState(itemCount);
  const [rows, setRows] = useState(1);
  const [rowHeight, setRowHeight] = useState(0);
  const containerElRef = useRef(null);
  const measureElRef = useRef(null);
  const observedElRef = useRef(null);
  const calculate = useCallback(() => {
    const container = containerElRef.current;
    const measure = measureElRef.current;
    if (!container || !measure) {
      return;
    }
    let availableWidth;
    if (observeParent && container.parentElement) {
      const parent = container.parentElement;
      const parentStyle = getComputedStyle(parent);
      availableWidth = parent.clientWidth - parseFloat(parentStyle.paddingLeft) - parseFloat(parentStyle.paddingRight);
    } else {
      availableWidth = container.offsetWidth;
    }
    const allChildren = Array.from(measure.children);

    // The measurement container holds itemCount items, plus optionally
    // an overflow indicator as the last child.
    const hasIndicator = allChildren.length > itemCount;
    const children = hasIndicator ? allChildren.slice(0, itemCount) : allChildren;
    const indicatorWidth = hasIndicator ? allChildren[allChildren.length - 1].offsetWidth : 0;
    if (children.length === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- overflow count is derived from measured DOM widths
      setVisibleCount(0);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- derived from measured DOM
      setRows(0);
      return;
    }
    const widths = children.map(child => child.offsetWidth);
    const measuredRowHeight = children.reduce((max, child) => Math.max(max, child.offsetHeight || 0), 0);
    const {
      visibleCount: nextVisible,
      rows: nextRows
    } = computeOverflow({
      widths,
      gap,
      availableWidth,
      indicatorWidth,
      minVisibleItems,
      maxVisibleItems,
      maxRows,
      collapseFrom
    });

    // eslint-disable-next-line @eslint-react/set-state-in-effect -- overflow count is derived from measured DOM widths
    setVisibleCount(nextVisible);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- derived from measured DOM
    setRows(prev => prev === nextRows ? prev : nextRows);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- derived from measured DOM
    setRowHeight(prev => prev === measuredRowHeight ? prev : measuredRowHeight);
  }, [itemCount, gap, minVisibleItems, maxVisibleItems, maxRows, collapseFrom, observeParent]);
  const containerRef = useCallback(el => {
    containerElRef.current = el;

    // Clean up previous observation
    if (observedElRef.current) {
      unobserveResize(observedElRef.current);
      observedElRef.current = null;
    }
    if (el) {
      const target = observeParent && el.parentElement ? el.parentElement : el;
      observeResize(target, () => {
        calculate();
      });
      observedElRef.current = target;
    }
  }, [calculate, observeParent]);
  const measureRef = useCallback(el => {
    measureElRef.current = el;
    if (el) {
      calculate();
    }
  }, [calculate]);

  // Recalculate when itemCount changes
  useIsomorphicLayoutEffect(() => {
    calculate();
  }, [calculate]);
  const hasOverflow = visibleCount < itemCount;
  return {
    containerRef,
    measureRef,
    visibleCount,
    hasOverflow,
    rows,
    rowHeight
  };
}